import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ICountryCode } from '@shared/types/phone-country-codes';
import parsePhoneNumberFromString, { AsYouType, parsePhoneNumber, parsePhoneNumberWithError } from 'libphonenumber-js';
import { defaultTo } from 'lodash';
import { BehaviorSubject, Subscription } from 'rxjs';

// Enum that contains the keys the user can use when typing it's phone number (besides numeric keys)
enum Keys {
  ARROW_LEFT = 'ArrowLeft',
  ARROW_RIGHT = 'ArrowRight',
  BACKSPACE = 'Backspace',
  TAB = 'Tab'
}

@Directive({
  selector: '[ravPhoneNumberFormatter]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => PhoneNumberFormatterDirective),
      multi: true
    }
  ]
})
export class PhoneNumberFormatterDirective implements OnInit, OnDestroy, ControlValueAccessor {
  @Input('ravPhoneNumberFormatter') onSelectedCountryCode$: BehaviorSubject<ICountryCode>;

  subscriptions: Subscription[] = [];

  private countryCode: ICountryCode;
  private _phoneNumber = '';
  private previousPhoneNumber = '';
  private defaultCountry: ICountryCode = {
    name: 'United States',
    countryCode: '+1',
    twoLetterCode: 'US'
  };

  // Defines the max length a phone number (without country code) can have according to the country selected
  private maxLengthCountryPhoneNumber: any = {
    us: 10,
    ca: 10
  };

  private allowedKeys: Array<string> = [
    'Backspace',
    'Delete',
    'Escape',
    'Enter',
    'Home',
    'End',
    'ArrowLeft',
    'ArrowRight',
    'Clear',
    'Copy',
    'Paste',
    'Tab'
  ];

  constructor(
    private _elementRef: ElementRef,
    private _renderer: Renderer2,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  @HostListener('input', ['$event']) onInputChange(e: Event) {
    let value = (<HTMLInputElement>e.target).value;
    value = value.replace(/\D+/g, '');
    this.formatPhoneNumber(value);
  }

  @HostListener('keydown', ['$event']) onKeyDown(e: KeyboardEvent) {
    const strLen = this._phoneNumber ? this._phoneNumber.length : 0;
    if (
      this.allowedKeys.indexOf(e.key) > -1 ||
      (e.key === 'c' && (e.ctrlKey || e.metaKey)) ||
      (e.key === 'v' && (e.ctrlKey || e.metaKey)) ||
      (e.key === 'x' && (e.ctrlKey || e.metaKey)) ||
      e.ctrlKey ||
      e.metaKey ||
      defaultTo(this.maxLengthCountryPhoneNumber[this.countryCode.twoLetterCode], 12) > strLen
    ) {
      // Allow default behavior
      return;
    } else {
      e.preventDefault();
    }
  }

  onChange = (_: any) => {};
  onTouched = () => {};

  // When a change comes from outside, this method is triggered
  writeValue(value: any): void {
    this.formatPhoneNumber(value, true);
  }

  registerOnChange(fn: (_: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._renderer.setProperty(this._elementRef.nativeElement, 'disabled', isDisabled);
  }

  ngOnInit() {
    this.subscriptions.push(
      this.onSelectedCountryCode$.subscribe((code) => {
        this.countryCode = code;
        this.formatPhoneNumber(this._phoneNumber);
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private formatPhoneNumber(phoneNumberStr: string, isSavedValue: boolean = false): void {
    let phoneNumber = !phoneNumberStr ? '' : phoneNumberStr;

    // if `isSavedValue`, most likely we're formatting an already existing phone number
    if (isSavedValue && phoneNumber) {
      const currentCountryCode = phoneNumber.slice(0, this.countryCode.countryCode.length);
      const parsedNumber = parsePhoneNumberFromString(
        phoneNumber,
        currentCountryCode !== this.countryCode.countryCode
          ? this.defaultCountry.twoLetterCode
          : <any>this.countryCode.twoLetterCode.toUpperCase()
      );

      try {
        const twoLetterCode = parsedNumber.country
          ? parsedNumber.country.toString()
          : currentCountryCode !== this.countryCode.countryCode
          ? this.defaultCountry.twoLetterCode
          : this.countryCode.twoLetterCode;
        const countryCode = parsedNumber.countryCallingCode.toString();
        this.countryCode = { name: '', countryCode, twoLetterCode };
      } catch (e) {
        console.error('Error when parsing saved number:', e);
      }
      phoneNumber = parsedNumber.nationalNumber.toString();
      // Since we received a previous saved phone number, we save the national number as the value "typed"
      phoneNumberStr = phoneNumber;
    }

    let parsedNormalizedNumber = '';
    try {
      parsedNormalizedNumber =
        phoneNumber && phoneNumber.length > 1
          ? parsePhoneNumber(phoneNumber, <any>this.countryCode.twoLetterCode.toUpperCase()).number.toString()
          : '';
    } catch (e) {
      console.error('Error when trying to parse a number', e);
    }

    const asYouType = new AsYouType(<any>this.countryCode.twoLetterCode.toUpperCase());
    const maskedPhoneNumber = asYouType.input(phoneNumber);

    if (parsedNormalizedNumber === '' || this.previousPhoneNumber !== parsedNormalizedNumber) {
      this.previousPhoneNumber = parsedNormalizedNumber;
      // Save the number
      this._phoneNumber = phoneNumberStr;
      // Update the model
      this.onChange(parsedNormalizedNumber);
      // Set the masked value for the input
      this._renderer.setProperty(this._elementRef.nativeElement, 'value', maskedPhoneNumber);
      this.changeDetectorRef.detectChanges();
    }
  }

  private handleKeyPress(e: KeyboardEvent) {
    e.preventDefault();
    // If we don't a phone number, we start formatting what we're receiving
    // if we have a phone number, we validate length, if we don't have a length defined, we use 12 as default
    // because e.164 allows phone numbers to have up to 12 digits (without country code)
    // @link https://en.wikipedia.org/wiki/E.164#:~:text=call%20origination.%5B2%5D-,As%20described%20in%20by,-the%20ITU%2C%20the
    if (
      !this._phoneNumber ||
      this._phoneNumber.length < defaultTo(this.maxLengthCountryPhoneNumber[this.countryCode.twoLetterCode], 12)
    ) {
      this.formatPhoneNumber(this._phoneNumber + e.key);
    }
  }

  private handleBackspaceKeyPress(e: KeyboardEvent) {
    e.preventDefault();
    this.formatPhoneNumber(
      this._phoneNumber.length === 0 ? '' : this._phoneNumber.slice(0, this._phoneNumber.length - 1)
    );
  }
}
